import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import { toast } from 'react-toastify';
import { PageHeader, FullPage, PrimaryButton } from '@tycoonlabs/web-components'
import { HeaderTitle, HeaderActionLoader, HeaderActionContainer } from 'components/reusables/AppStyles';

import BalancesActions from './BalancesActions';
import BalancesTable from './BalancesTable';
import Spinner from '../../reusables/Spinner';
import Session from '../../../utils/session';
import { exportBalancesToCSV } from 'helpers';

import { FETCH_BALANCES_QUERY } from 'graphql/queries';

const Balances = () => {
  const { companyId, accountId } = useParams();
  // DATE PICKER HANDLERS AND STATE FOR FILTER
  const [dateRange, setDateRange] = useState({
    from: moment()
      .subtract(1, 'months')
      .toDate(),
    to: moment().toDate()
  });
  let [pickerRef, setPickerRef] = useState(null);

  const showFromMonth = () => {
    const { from, to } = dateRange;
    if (!from) {
      return;
    }
    if (moment(to).diff(moment(from), 'months') < 2) {
      pickerRef.getDayPicker().showMonth(from);
    }
  };

  const handleFromChange = from => {
    setDateRange({ ...dateRange, from });
  };

  const handleToChange = to => {
    setDateRange({ ...dateRange, to });
  };

  const handleFromTodayButton = from => {
    setDateRange({ ...dateRange, from });
    pickerRef.getInput().focus();
  };

  const handleToTodayButton = to => {
    setDateRange({ ...dateRange, to });
    pickerRef.hideDayPicker();
  };

  // BALANCES STATE
  const [balances, setBalances] = useState([]);
  const [currentBalance, setCurrentBalance] = useState('');
  const [filteredBalances, setFilteredBalances] = useState(balances);

  useEffect(() => {
    setFilteredBalances(
      balances.filter(balance =>
        moment(balance.date).isBetween(dateRange.from, dateRange.to)
      )
    );
  }, [balances, dateRange]);

  const notifyError = () =>
    toast('Something went wrong retrieving balances, please try again');

  const account = Session.getPlaidAccount(companyId, accountId);

  const { loading } = useQuery(FETCH_BALANCES_QUERY, {
    variables: { plaid_account_id: accountId, company_id: companyId },
    onCompleted: data => {
      setBalances(data.getAccountBalances.balances);
      setCurrentBalance(data.getAccountBalances.current_balance);
    },
    onError: () => notifyError()
  });

  let accountLabel = `${account.institution_name} - ${account.name}${' '}`;
  accountLabel += account.mask ? `(...${account.mask}) ` : ''
  accountLabel += account.official_name ? `${account.official_name}` : ''
  const accountName = `${account.institution_name} - ${account.name} ${account.mask ? ` (...${account.mask})` : ``
    }`;
  return (
    <>
      <PageHeader>
        <HeaderTitle title={accountLabel} back="Back to accounts" to={`/accounts/${companyId}`} />
        <HeaderActionContainer>
          {loading && (
            <HeaderActionLoader />
          )}
          <PrimaryButton size="medium" color="secondary" onClick={() => exportBalancesToCSV(filteredBalances, accountName)}>
            Export
          </PrimaryButton>
        </HeaderActionContainer>
      </PageHeader>
      <FullPage>
        <BalancesActions
          dateRange={dateRange}
          pickerRef={pickerRef}
          setPickerRef={setPickerRef}
          showFromMonth={showFromMonth}
          handleFromChange={handleFromChange}
          handleToChange={handleToChange}
          handleFromTodayButton={handleFromTodayButton}
          handleToTodayButton={handleToTodayButton}
          accountId={accountId}
          balances={filteredBalances}
          currentBalance={currentBalance}
        />
        <BalancesTable loading={loading} balances={filteredBalances} />
      </FullPage>
    </>
  );
};

export default Balances;
